import React, { Component } from 'react';
import './App.css';
import './theme/library.css';

import { NavLink } from 'react-router-dom';

import { connect } from 'react-redux';
import { handleSetBreadcrumbName } from './redux/actions/breadcrumbActions';

// Cover art (1600x900, webp). Bundled so webpack fingerprints the URLs — swapping
// a cover picks up immediately, and a missing file fails the build loudly.
import volatileArt from './covers/volatile.webp';
import deadInsideArt from './covers/deadinside.webp';
import peysArt from './covers/peys.webp';
import misanthropyArt from './covers/misanthropy.webp';

// Each title is a self-describing record; the tile AND the lightbox render entirely
// from it. `interfaces` declares the ways to play — a title with 'android' grows a
// second action, a blue-screen-only title has just 'browser'. `art` is the cover:
// a url(...) to the bundled webp; the commented radial-gradient is the fallback look.
//
// DEFERRED (noted with the owner): `progress` (a "Continue playing" shelf + tile
// bar) should derive from saved game score or Milestones — not built yet. `androidUrl`
// is null until a direct APK is hosted (Play Store listing is the eventual path), so
// the app action shows as "coming soon".
const TITLES = [
	{
		id: 'volatile', route: '/bookshelf/default', crumbKey: 'bookshelf', crumb: 'Volatile',
		title: 'Volatile', genre: 'Interactive Fiction', state: 'owned',
		art: `url(${volatileArt})`, // fallback: radial-gradient(130% 150% at 18% 8%, #7a2417, #180f0e 72%)
		// Phone-sim is Volatile's INTENDED interface (owner's call); browser is the full
		// alternate. Ordered browser-first only because the APK isn't hosted yet, so
		// browser is the currently-playable action — flip to app-primary once androidUrl lands.
		interfaces: ['browser', 'android'], androidUrl: null,
		meta: ['Interactive Fiction', 'Smartphone', 'Text-driven', 'Urban Fantasy', 'Horror', 'Vampires'],
		blurb: 'When her roommate mysteriously disappears, a headstrong college student searches Cincinnati for answers and stumbles into a hidden world of vampires, dangerous secrets, and a stranger she can’t seem to stay away from.',
	},
	{
		id: 'deadinside', route: '/bookshelf/story2', crumbKey: 'bookshelf', crumb: 'Dead Inside',
		title: 'Dead Inside', genre: 'Interactive Fiction', state: 'demo',
		art: `url(${deadInsideArt})`, // fallback: radial-gradient(130% 150% at 82% 8%, #4a201c, #150f0e 72%)
		interfaces: ['browser'],
		meta: ['Interactive Fiction', 'Text-driven', 'Survival', 'Horror', 'Resource Management', 'Character Progression', 'Zombie Apocalypse'],
		blurb: 'After barely escaping your apartment, you find refuge in a neighborhood pub, where a handful of strangers, dwindling supplies, and the infected outside give you something you never expected: people worth surviving for.',
	},
	{
		id: 'peys', route: '/bookshelf/story1', crumbKey: 'bookshelf', crumb: 'Please Enjoy Your Stay',
		title: 'Please Enjoy Your Stay', genre: 'Interactive Fiction', state: 'demo',
		art: `url(${peysArt})`, // fallback: radial-gradient(130% 150% at 50% 0%, #40301a, #150f0e 72%)
		interfaces: ['browser'],
		meta: ['Interactive Fiction', 'Text-driven', 'Supernatural', 'Erotic'],
		blurb: 'When you accidentally summon a surprisingly receptive succubus in your mediocre hotel room, an otherwise forgettable business trip takes a turn you definitely won’t be putting on the expense report.',
	},
	{
		id: 'misanthropy', route: '/misanthropy', crumbKey: 'misanthropy', crumb: 'Misanthropy',
		title: 'Misanthropy', genre: 'TTRPG Sandbox', state: 'demo',
		art: `url(${misanthropyArt})`, // fallback: radial-gradient(130% 150% at 30% 18%, #3a2440, #150f0e 72%)
		interfaces: ['browser'],
		meta: ['TTRPG Sandbox', 'AI PLayers', 'Game Master', 'Campaign Testing'],
		blurb: 'Your campaign is ready. Your players aren’t. Misanthropy puts AI personalities around your table who roll player characters, make choices, argue over terrible plans, fall in love with your NPCs, and actually show up for game night. You bring the world. They come to play.',
	},
];

const IFACE_BADGE = { browser: 'WEB', android: 'APP' };

class Home extends Component {
	constructor(props) {
		super(props);
		this.state = { openId: null };
		this.handleSetBreadcrumbName = this.props.handleSetBreadcrumbName.bind(this);
		this.onKey = this.onKey.bind(this);
	}

	componentDidMount() {
		document.addEventListener('keydown', this.onKey);
	}

	componentWillUnmount() {
		document.removeEventListener('keydown', this.onKey);
	}

	onKey(e) {
		if (e.key === 'Escape') {
			this.setState({ openId: null });
		}
	}

	openBox(id) {
		this.setState({ openId: id });
	}

	closeBox() {
		this.setState({ openId: null });
	}

	beforeLeaving(crumbKey, crumb) {
		this.handleSetBreadcrumbName({ key: '/' + crumbKey, value: crumb });
	}

	renderTile(t) {
		let self = this;
		return (
			<div className="tile" key={t.id} tabIndex={0} role="button"
				style={{ '--art': t.art }}
				onClick={function () { self.openBox(t.id); }}
				onKeyDown={function (e) { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); self.openBox(t.id); } }}
			>
				{
					t.state === 'owned'
						? <span className="tile-chip chip-owned">IN LIBRARY</span>
						: <span className="tile-chip chip-demo">DEMO</span>
				}
				<div className="tile-badges">
					{t.interfaces.map(function (i) { return <span className="iface-badge" key={i}>{IFACE_BADGE[i]}</span>; })}
				</div>
				<div className="tile-title">{t.title}</div>
			</div>
		);
	}

	// The browser interface is always playable; the app interface is "coming soon"
	// until androidUrl is set. So browser takes the primary (accent) button for now.
	renderAction(t, iface) {
		if (iface === 'browser') {
			return (
				<NavLink key="browser" className="lib-btn lib-btn-primary" to={t.route}
					onClick={this.beforeLeaving.bind(this, t.crumbKey, t.crumb)}
				>
					{'▸'}&nbsp; {t.state === 'owned' ? 'Play in browser' : 'Play demo (browser)'}
				</NavLink>
			);
		}
		if (iface === 'android') {
			if (t.androidUrl) {
				return (
					<a key="android" className="lib-btn lib-btn-ghost" href={t.androidUrl}>
						{'⤓'}&nbsp; Download the Android app
					</a>
				);
			}
			return (
				<button key="android" className="lib-btn lib-btn-soon" type="button" disabled>
					{'⤓'}&nbsp; Android app &mdash; coming soon
				</button>
			);
		}
		return null;
	}

	renderLightbox() {
		let self = this;
		const t = TITLES.find(function (x) { return x.id === self.state.openId; });
		if (!t) {
			return null;
		}
		return (
			<div className="lb-backdrop" role="dialog" aria-modal="true"
				onClick={function (e) { if (e.currentTarget === e.target) self.closeBox(); }}
			>
				<div className="lb">
					<div className="lb-hero" style={{ '--art': t.art }}>
						<button className="lb-x" aria-label="Close" onClick={function () { self.closeBox(); }}>{'×'}</button>
						<div className="lb-hero-title">{t.title}</div>
					</div>
					<div className="lb-body">
						<div className={'lb-state' + (t.state === 'owned' ? ' owned' : '')}>
							{t.state === 'owned' ? '✓ In your library' : 'Free demo · buy to continue past the wall'}
						</div>
						<div className="lb-actions">
							{t.interfaces.map(function (i) { return self.renderAction(t, i); })}
						</div>
						{
							t.interfaces.length > 1 &&
							<p className="iface-note">
								Volatile is designed for the phone &mdash; the Android app is the intended experience (coming soon).
								You can also play the full story right here in the browser terminal. Your progress is yours; the choice is yours.
							</p>
						}
						<div className="lb-meta">
							{t.meta.map(function (m, i) {
								return (
									<React.Fragment key={m}>
										{i > 0 && <span className="lb-meta-sep" aria-hidden="true">&bull;</span>}
										<span>{m}</span>
									</React.Fragment>
								);
							})}
						</div>
						<p className="lb-blurb">{t.blurb}</p>
					</div>
				</div>
			</div>
		);
	}

	render() {
		let self = this;
		return (
			<div className="lib-page">
				<div className="lib-inner">
					<p className="lib-kicker">Newfangle Press</p>
					<h1 className="lib-h1">My Library</h1>
					<p className="lib-lede">Free demos, open to any account. Pick up wherever you left off.</p>

					<h2 className="lib-shelf-h">Your titles</h2>
					<div className="lib-shelf">
						{TITLES.map(function (t) { return self.renderTile(t); })}
					</div>
				</div>
				{this.renderLightbox()}
			</div>
		);
	}
}

const mapDispatchToProps = (dispatch) => {
	return {
		handleSetBreadcrumbName: (breadcrumb) => {
			dispatch(handleSetBreadcrumbName(breadcrumb));
		},
	};
};

const mapStateToProps = (state) => {
	return {};
};

export default connect(mapStateToProps, mapDispatchToProps)(Home);
